import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Download, Lock, FileText, Building2, MapPin, Phone, Globe, ChevronDown, ChevronUp, Sparkles, Loader2 } from 'lucide-react';
import TrafficLightIndicator from '../components/TrafficLightIndicator';
import { motion } from 'framer-motion';

const AssessmentSummary = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedAreas, setExpandedAreas] = useState({});
  const [generatingGuide, setGeneratingGuide] = useState(false);
  const [existingGuide, setExistingGuide] = useState(null);

  useEffect(() => {
    fetchData();
    checkExistingGuide();
  }, [submissionId]);

  const checkExistingGuide = async () => {
    try {
      const guideRes = await api.get(`/submissions/${submissionId}/guide`);
      setExistingGuide(guideRes.data);
    } catch (e) {
      // No guide exists yet - that's fine
      setExistingGuide(null);
    }
  };

  const handleGenerateGuide = async () => {
    setGeneratingGuide(true);
    try {
      const response = await api.post(`/submissions/${submissionId}/generate-guide`);
      toast.success('Guide generated successfully!');
      navigate(`/guide/${submissionId}`);
    } catch (error) {
      console.error('Guide generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate guide');
    } finally {
      setGeneratingGuide(false);
    }
  };

  const fetchData = async () => {
    try {
      const summaryRes = await api.get(`/submissions/${submissionId}/summary`);
      setSummary(summaryRes.data);
      
      // Check if report is available
      if (summaryRes.data.report_available === false) {
        // Report is not available yet - stay on this page but show message
        setLoading(false);
        return;
      }
      
      // Also fetch venue details for profile
      if (summaryRes.data.venue_id) {
        try {
          const venueRes = await api.get(`/venues/${summaryRes.data.venue_id}`);
          setVenue(venueRes.data);
        } catch (e) {
          console.log('Could not fetch venue details');
        }
      }
    } catch (error) {
      toast.error('Failed to load assessment summary');
    } finally {
      setLoading(false);
    }
  };

  const handleExportJSON = async () => {
    try {
      const response = await api.get(`/submissions/${submissionId}/export`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `nysa_submission_${submissionId}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('JSON exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export JSON');
    }
  };

  const handlePrintReport = () => {
    window.print();
  };

  const toggleAreaExpanded = (areaId) => {
    setExpandedAreas(prev => ({
      ...prev,
      [areaId]: !prev[areaId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Report not available - show appropriate message
  if (summary?.report_available === false) {
    return (
      <div className="min-h-screen p-6 md:p-12">
        <div className="max-w-3xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-6 rounded-full"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <div className="bg-card rounded-xl border border-border/50 shadow-lg p-8">
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-6 bg-teal-100 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8 text-teal-600" />
              </div>
              <h1 className="text-2xl font-bold mb-4">Assessment In Progress</h1>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {summary?.message || "Your assessment is still being processed. Please check back shortly."}
              </p>
              
              <Button
                onClick={() => navigate('/dashboard')}
                className="rounded-full"
              >
                Return to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getImpactLabel = (trafficLight) => {
    if (trafficLight === 'green') return 'Low';
    if (trafficLight === 'amber') return 'Moderate';
    return 'High';
  };

  const getImpactColor = (trafficLight) => {
    if (trafficLight === 'green') return 'bg-emerald-500';
    if (trafficLight === 'amber') return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="min-h-screen p-6 md:p-12 print:p-4">
      <div className="max-w-5xl mx-auto">
        <div className="print:hidden">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-6 rounded-full"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        {/* Header Card */}
        <div className="bg-card rounded-xl border border-border/50 shadow-lg p-8 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight mb-2" data-testid="summary-title">
                Sensory Assessment Report
              </h1>
              <p className="text-xl text-muted-foreground">{summary?.venue_name}</p>
              <p className="text-sm text-muted-foreground mt-1">
                Submission ID: {submissionId}
              </p>
            </div>
            {summary?.is_locked && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-800 rounded-full text-sm font-medium">
                <Lock className="w-4 h-4" />
                Submitted & Locked
              </div>
            )}
          </div>

          {/* SaaS Workflow - Guide Generation Section - PROMINENT CTA */}
          {summary?.is_locked && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-2xl p-6 mb-8 shadow-sm"
            >
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-100 rounded-full">
                    <Sparkles className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-emerald-900">
                      {existingGuide ? 'Your Sensory Guide is Ready' : 'Assessment Complete'}
                    </h3>
                    <p className="text-sm text-emerald-700 mt-1">
                      {existingGuide 
                        ? 'View your branded accessibility guide for visitors.'
                        : 'Generate your accessibility guide instantly.'}
                    </p>
                  </div>
                </div>
                
                {/* Primary CTA Button - Large and Prominent */}
                {existingGuide ? (
                  <Button
                    onClick={() => navigate(`/guide/${submissionId}`)}
                    size="lg"
                    className="rounded-full bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white px-8 py-3 text-base font-semibold shadow-md hover:shadow-lg transition-all"
                    data-testid="view-guide-button"
                  >
                    <FileText className="w-5 h-5 mr-2" />
                    View Sensory Guide
                  </Button>
                ) : (
                  <Button
                    onClick={handleGenerateGuide}
                    disabled={generatingGuide}
                    size="lg"
                    className="rounded-full bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white px-8 py-3 text-base font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-70"
                    data-testid="generate-guide-button"
                  >
                    {generatingGuide ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Generating your guide...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Generate Sensory Guide
                      </>
                    )}
                  </Button>
                )}
              </div>
            </motion.div>
          )}

          {/* Export buttons temporarily hidden
          <div className="flex gap-4 print:hidden">
            <Button
              onClick={handlePrintReport}
              variant="default"
              className="rounded-full"
              data-testid="print-report-button"
            >
              <FileText className="w-4 h-4 mr-2" />
              Print / Save as PDF
            </Button>
            <Button
              onClick={handleExportJSON}
              variant="outline"
              className="rounded-full"
              data-testid="export-json-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Download JSON
            </Button>
          </div>
          */}
        </div>

        {/* Venue Profile */}
        {venue && (
          <div className="bg-card rounded-xl border border-border/50 shadow-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-semibold">Venue Profile</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <Building2 className="w-4 h-4 mt-1 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{venue.name}</p>
                  </div>
                </div>
                {venue.address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 mt-1 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Address</p>
                      <p className="font-medium">{venue.address}</p>
                    </div>
                  </div>
                )}
                {venue.contact_info && (
                  <div className="flex items-start gap-2">
                    <Phone className="w-4 h-4 mt-1 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Contact</p>
                      <p className="font-medium">{venue.contact_info}</p>
                    </div>
                  </div>
                )}
                {venue.website && (
                  <div className="flex items-start gap-2">
                    <Globe className="w-4 h-4 mt-1 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Website</p>
                      <a href={venue.website} target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                        {venue.website}
                      </a>
                    </div>
                  </div>
                )}
              </div>
              {venue.description && (
                <div className="md:col-span-2 pt-4 border-t">
                  <p className="text-sm text-muted-foreground mb-1">About</p>
                  <p className="text-sm">{venue.description}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Venue Context Overview */}
        {(summary?.venue_type || summary?.primary_environment || summary?.entry_models?.length > 0) && (
          <div className="bg-card rounded-xl border border-border/50 shadow-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-semibold">Venue Context Overview</h2>
            </div>
            <div className="space-y-3">
              {summary?.venue_type && (
                <div>
                  <p className="text-sm text-muted-foreground">Venue Type</p>
                  <p className="font-medium">{summary.venue_type}</p>
                </div>
              )}
              {summary?.primary_environment && (
                <div>
                  <p className="text-sm text-muted-foreground">Primary Environment</p>
                  <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                    {summary.primary_environment}
                  </span>
                </div>
              )}
              {summary?.structural_characteristics?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground">Structural Characteristics</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {summary.structural_characteristics.map((char, idx) => (
                      <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                        {char}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {summary?.entry_models?.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground">Entry Model(s)</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {summary.entry_models.map((model, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {model}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Design Brief - REMOVED from UI (backend preserved) */}

        {/* Areas Assessment */}
        <div className="bg-card rounded-xl border border-border/50 shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Area Assessments</h2>
          
          <div className="space-y-4">
            {summary?.areas?.map((area, index) => {
              const isExpanded = expandedAreas[area.area_id] ?? true;
              
              return (
                <motion.div
                  key={area.area_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border rounded-lg overflow-hidden"
                >
                  {/* Area Header */}
                  <div 
                    className="p-4 bg-secondary/30 cursor-pointer flex items-center justify-between"
                    onClick={() => toggleAreaExpanded(area.area_id)}
                  >
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold">{area.area_name}</h3>
                      <TrafficLightIndicator score={Math.round((area.area_total / 7) * 100) / 100} />
                    </div>
                    <div className="flex items-center gap-2 print:hidden">
                      {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </div>
                  
                  {/* Area Details */}
                  {(isExpanded || true) && (
                    <div className={`p-4 space-y-4 ${!isExpanded ? 'hidden print:block' : ''}`}>
                      {/* Area Summary / Purpose */}
                      {area.area_summary && (
                        <div className="text-sm text-muted-foreground italic mb-2">
                          {area.area_summary}
                        </div>
                      )}
                      
                      {/* Sensory Overview */}
                      {area.sensory_overview && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                          <h4 className="text-sm font-semibold text-blue-900 mb-1">Sensory Overview</h4>
                          <p className="text-sm text-blue-800">{area.sensory_overview}</p>
                        </div>
                      )}
                      
                      {/* Support Indicators */}
                      {area.support_indicators && Object.values(area.support_indicators).some(v => v) && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {area.support_indicators.seating_available && (
                            <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm">Seating Available</span>
                          )}
                          {area.support_indicators.step_free_access && (
                            <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm">Step-Free Access</span>
                          )}
                          {area.support_indicators.accessible_toilet_nearby && (
                            <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full text-sm">Accessible Toilet</span>
                          )}
                          {area.support_indicators.staff_present && (
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">Staff Present</span>
                          )}
                          {area.support_indicators.assistance_available && (
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">Assistance Available</span>
                          )}
                          {area.support_indicators.quiet_space_nearby && (
                            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">Quiet Space Nearby</span>
                          )}
                          {area.support_indicators.lower_lighting_available && (
                            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">Lower Lighting</span>
                          )}
                          {area.support_indicators.calm_waiting_area && (
                            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">Calm Waiting Area</span>
                          )}
                          {area.support_indicators.clear_signage && (
                            <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">Clear Signage</span>
                          )}
                          {area.support_indicators.map_available && (
                            <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm">Map Available</span>
                          )}
                          {area.support_indicators.timed_entry_available && (
                            <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm">Timed Entry</span>
                          )}
                          {area.support_indicators.managed_queue_system && (
                            <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm">Managed Queue</span>
                          )}
                        </div>
                      )}
                      
                      {/* Category Breakdown */}
                      {Object.entries(area.category_breakdown).map(([category, data]) => (
                        <div key={category} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium">{category}</h4>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">{data.total}/12</span>
                              <span className={`px-2 py-0.5 rounded text-xs text-white font-medium ${getImpactColor(data.traffic_light)}`}>
                                {getImpactLabel(data.traffic_light)}
                              </span>
                            </div>
                          </div>
                          
                          {/* Individual Prompts */}
                          {data.prompts && data.prompts.length > 0 && (
                            <div className="space-y-2 text-sm">
                              {data.prompts.map((prompt, pIdx) => (
                                <div key={pIdx} className="flex justify-between items-start py-2 border-b last:border-0">
                                  <span className="text-muted-foreground flex-1 pr-4">{prompt.question}</span>
                                  <div className="flex items-center gap-3 flex-shrink-0">
                                    <span className="text-right min-w-24">{prompt.answer}</span>
                                    <span className="font-mono font-bold w-6 text-center">{prompt.score}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Venue-Specific Sensory Notes */}
                          {data.sensory_notes && (
                            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-xs font-medium text-amber-800 mb-1">Venue-Specific Notes:</p>
                              <p className="text-sm text-amber-900">{data.sensory_notes}</p>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {/* Photos */}
                      {area.photos && area.photos.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-medium mb-2">Photos</p>
                          <div className="flex gap-2 flex-wrap">
                            {area.photos.map((photo, pIdx) => (
                              <a 
                                key={pIdx} 
                                href={photo.url || photo} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="block"
                              >
                                <img 
                                  src={photo.url || photo} 
                                  alt={`${area.area_name} photo ${pIdx + 1}`}
                                  className="w-24 h-24 object-cover rounded border hover:opacity-80 transition"
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentSummary;
